
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Check, 
  Plus, 
  Loader2,
  Building2,
  Store as StoreIcon,
  Users,
  Clock,
  PartyPopper,
  MessageCircle,
  Copy,
  Trash2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { api, ShiftType, Staff, Store as StoreModel, Position } from '../api';
import { useAuth } from '../contexts/AuthContext';

const STEPS = [
    { id: 1, name: '企業情報', icon: Building2 },
    { id: 2, name: '店舗登録', icon: StoreIcon },
    { id: 3, name: '連絡手段', icon: MessageCircle },
    { id: 4, name: 'ポジション', icon: Users },
    { id: 5, name: '完了', icon: PartyPopper }
];

const SetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [adminStaffId, setAdminStaffId] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);

  const [companyName, setCompanyName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [positions, setPositions] = useState<Position[]>([]);
  const [newPositionName, setNewPositionName] = useState('');
  
  const [inviteUrl, setInviteUrl] = useState('');
  const [isLineLinked, setIsLineLinked] = useState(false);
  // スタッフへの連絡手段（true＝LINEを使う、false＝LINEを使わない）。
  const [useLine, setUseLine] = useState(true);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [generationError, setGenerationError] = useState(false);
  const tokenGeneratedRef = useRef(false);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}|null>(null);

  useEffect(() => {
    if (!currentUser) return;
    const restoreProgress = async () => {
        try {
            const stores = await api.getStores();
            if (stores.length > 0) {
                const s = stores[0];
                setStoreId(s.id);
                setStoreName(s.name);
                setCompanyName(s.companyName);
                setAdminStaffId(currentUser.uid);

                const loadedPos = await api.getPositions();
                setPositions(loadedPos);

                // getStaff() のフィルタリングに失敗する場合に備え、
                // 管理者自身のドキュメントを直接取得する
                const staffList = await api.getStaff();
                const me = staffList.find(s => s.id === currentUser.uid);

                if (s.lineEnabled === false) setUseLine(false);
                if (me) {
                    setAdminLastName(me.lastName || '');
                    setAdminFirstName(me.firstName || '');
                    if (me.lineStatus === 'connected' || s.lineEnabled === false) {
                        setIsLineLinked(true);
                        if (currentStep < 4) setCurrentStep(4);
                    } else {
                        if (currentStep < 3) setCurrentStep(3);
                    }
                }
                // スタッフが見つからない場合はステップ1のまま（デフォルト）
            }
        } catch (e) {
            console.error("[SETUP] Restore error:", e);
        } finally {
            setIsInitialLoading(false);
        }
    };
    restoreProgress();
  }, [currentUser]);
  
  const generateToken = async () => {
    if (!adminStaffId) return;
    setIsGeneratingToken(true);
    setGenerationError(false);
    try {
        const url = await api.generateInviteToken(adminStaffId);
        setInviteUrl(url);
        tokenGeneratedRef.current = true;
    } catch (error) {
        console.error('[SETUP] URL Generation Error:', error);
        setGenerationError(true);
    } finally {
        setIsGeneratingToken(false);
    }
  };

  useEffect(() => {
    if (currentStep === 3 && useLine && adminStaffId && !tokenGeneratedRef.current && !isLineLinked) {
      generateToken();
    }
  }, [currentStep, adminStaffId, isLineLinked, useLine]);

  useEffect(() => {
      if (!adminStaffId || currentStep !== 3) return;
      const unsubscribe = api.listenToStaffProfile(adminStaffId, (staff) => {
        if (staff?.lineStatus === 'connected') setIsLineLinked(true);
      });
      return () => unsubscribe();
  }, [adminStaffId, currentStep]);

  const checkLineStatus = async () => {
      if (!adminStaffId) return;
      setIsCheckingStatus(true);
      try {
          const staffList = await api.getStaff();
          const me = staffList.find(s => s.id === adminStaffId);
          if (me && me.lineStatus === 'connected') {
              setIsLineLinked(true);
              showToast('連携を確認しました。');
          } else {
              showToast('まだ連携が確認できません。', 'info');
          }
      } catch(e) { console.error(e); } finally { setIsCheckingStatus(false); }
  };

  useEffect(() => {
      if (currentStep === 3 && useLine && isLineLinked) {
          const timer = setTimeout(() => nextStep(), 1500);
          return () => clearTimeout(timer);
      }
  }, [currentStep, isLineLinked]);

  const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
      setToast({ show: true, message: msg, type: type === 'info' ? 'success' : type });
      setTimeout(() => setToast(null), 3000);
  };

  const nextStep = () => setCurrentStep(c => Math.min(c + 1, STEPS.length));
  const prevStep = () => setCurrentStep(c => Math.max(c - 1, 1));
  
  const canGoNext = useMemo(() => {
      switch(currentStep) {
          case 1: return companyName.trim() !== '' && adminLastName.trim() !== '' && adminFirstName.trim() !== '';
          case 2: return storeName.trim() !== '';
          case 3: return !useLine || isLineLinked;
          default: return true;
      }
  }, [currentStep, companyName, adminLastName, adminFirstName, storeName, isLineLinked, useLine]);

  // 連絡手段の選択を店舗に保存してから次へ進む。
  const handleSaveChannelAndNext = async () => {
      if (!storeId) { nextStep(); return; }
      setIsLoading(true);
      try {
          const stores = await api.getStores();
          const target = stores.find(st => st.id === storeId);
          if (target) await api.saveStores([{ ...target, lineEnabled: useLine }]);
          nextStep();
      } catch (e) {
          showToast('保存に失敗しました。', 'error');
      } finally { setIsLoading(false); }
  };

  const handleSaveStoreAndAdmin = async () => {
      if(!storeName.trim() || !companyName.trim() || !currentUser) return;
      setIsLoading(true);
      const idToUse = storeId || `store_${Date.now()}`;
      const newStore: StoreModel = {
          id: idToUse, name: storeName, plan: 'standard', createdAt: new Date().toISOString(),
          ownerId: currentUser.uid, companyName: companyName,
          closingDate: 25,
      };
      try {
          await api.saveStores([newStore]);
          setStoreId(idToUse); 
          const adminStaff: Staff = {
              id: currentUser.uid, lastName: adminLastName, firstName: adminFirstName,
              hourlyWage: 0, limit: 0, insuranceType: 'none', tags: ['管理者'], avatarColor: 'bg-slate-800',
              lineStatus: 'unconnected', stores: [newStore.id], isGuest: false, skills: [], positionIds: [],
              employmentType: 'full_time', role: 'system_admin', priority: 'high', paidLeaveCount: 0
          };
          await api.saveStaff([adminStaff]);
          setAdminStaffId(currentUser.uid);
          nextStep();
      } catch(e: unknown) {
          console.error(e);
          showToast(e instanceof Error ? e.message : '店舗の保存に失敗しました', 'error');
      } finally { setIsLoading(false); }
  };
  
  const handleAddPosition = async () => {
    if (!newPositionName.trim() || !storeId) return;
    const newPosition: Position = { id: `pos_${Date.now()}`, name: newPositionName, color: 'bg-blue-100 text-blue-800', storeIds: [storeId] };
    try {
        await api.savePositions([...positions, newPosition]);
        setPositions(prev => [...prev, newPosition]);
        setNewPositionName('');
    } catch(e) { showToast('追加に失敗しました。', 'error'); }
  };

  const handleCompleteSetup = async () => {
    setIsLoading(true);
    try {
        await api.completeSetup();
        navigate('/admin/shifts');
    } catch (e) {
        showToast('完了処理に失敗しました。', 'error');
    } finally {
        setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={32} />
        <p className="text-slate-400 text-xs font-bold tracking-widest">セットアップを準備中...</p>
      </div>
    );
  }
  
  const renderStepContent = () => {
      switch(currentStep) {
          case 1: return (
              <div className="max-w-md mx-auto py-8 text-center animate-fade-in-up px-4">
                  <h2 className="text-xl font-bold text-slate-800 mb-2">企業情報の登録</h2>
                  <p className="text-sm text-slate-500 mb-8">企業名（屋号）と担当者名を登録します。</p>
                  <div className="bg-slate-50 p-6 rounded-[5px] border border-slate-200 space-y-4 text-left">
                       <div>
                          <label className="text-xs font-bold text-slate-500 mb-1 block">企業名（または屋号）</label>
                          <input type="text" value={companyName} placeholder="株式会社ユルリカ" onChange={(e) => setCompanyName(e.target.value)} className="w-full border border-slate-300 rounded-[4px] px-4 py-2.5 focus:ring-2 focus:ring-blue-200 outline-none font-bold" />
                       </div>
                       <div>
                          <label className="text-xs font-bold text-slate-500 mb-1 block">担当者名（あなた）</label>
                          <div className="flex gap-2">
                            <input type="text" value={adminLastName} placeholder="姓" onChange={(e) => setAdminLastName(e.target.value)} className="w-1/2 border border-slate-300 rounded-[4px] px-4 py-2.5 focus:ring-2 focus:ring-blue-200 outline-none font-bold" />
                            <input type="text" value={adminFirstName} placeholder="名" onChange={(e) => setAdminFirstName(e.target.value)} className="w-1/2 border border-slate-300 rounded-[4px] px-4 py-2.5 focus:ring-2 focus:ring-blue-200 outline-none font-bold" />
                          </div>
                       </div>
                  </div>
              </div>
          );
          case 2: return (
              <div className="max-w-md mx-auto py-8 text-center animate-fade-in-up px-4">
                  <h2 className="text-xl font-bold text-slate-800 mb-2">最初の店舗を登録</h2>
                  <p className="text-sm text-slate-500 mb-8">管理する最初の店舗を登録しましょう。</p>
                  <div className="bg-slate-50 p-6 rounded-[5px] border border-slate-200 text-left">
                       <label className="text-xs font-bold text-slate-500 mb-1 block">店舗名</label>
                       <input type="text" value={storeName} placeholder="渋谷本店" onChange={(e) => setStoreName(e.target.value)} className="w-full border border-slate-300 rounded-[4px] px-4 py-2.5 focus:ring-2 focus:ring-blue-200 outline-none font-bold" />
                  </div>
              </div>
          );
          case 3: return (
              <div className="max-w-md mx-auto py-8 text-center animate-fade-in-up px-4">
                  <h2 className="text-xl font-bold text-slate-800 mb-2">スタッフへの連絡手段</h2>
                  <p className="text-sm text-slate-500 mb-6 font-bold">シフトの提出依頼や確定のお知らせを届ける方法を選びます。後から設定画面で変更できます。</p>
                  <div className="grid grid-cols-2 gap-2 mb-6 text-left" data-testid="setup-channel">
                      {[
                          { value: true, title: 'LINEを使う', desc: 'スタッフはLINEから連携してログインします。' },
                          { value: false, title: 'LINEを使わない', desc: 'メールで招待し、ID・パスワードでログインします。' },
                      ].map(opt => (
                          <button key={String(opt.value)} type="button" onClick={() => setUseLine(opt.value)}
                              className={`p-3 rounded-[5px] border transition-all ${useLine === opt.value ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-100' : 'bg-white border-slate-300 hover:border-slate-400'}`}>
                              <span className={`block text-sm font-bold ${useLine === opt.value ? 'text-blue-700' : 'text-slate-700'}`}>{opt.title}</span>
                              <span className="block text-[11px] text-slate-500 leading-relaxed mt-1">{opt.desc}</span>
                          </button>
                      ))}
                  </div>
                  {!useLine ? (
                      <div className="bg-slate-50 p-6 rounded-[5px] border border-slate-200 text-left">
                          <p className="text-sm font-bold text-slate-700 mb-2">LINEを使わない運用</p>
                          <ul className="text-xs text-slate-500 leading-relaxed space-y-1 list-disc pl-4">
                              <li>スタッフを登録すると、招待メールが届きます。</li>
                              <li>スタッフはメールのURLからパスワードを設定し、ID・パスワードでログインします。</li>
                              <li>提出依頼・確定・助っ人募集は、メールとアプリ内のお知らせで届きます。</li>
                          </ul>
                      </div>
                  ) : isLineLinked ? (
                      <div className="bg-green-50 p-8 rounded-[5px] border border-green-200 flex flex-col items-center justify-center">
                          <CheckCircle2 size={48} className="text-green-500 mb-4 animate-bounce" />
                          <p className="font-bold text-green-700">連携が完了しました。</p>
                      </div>
                  ) : (
                      <div className="bg-slate-50 p-6 rounded-[5px] border border-slate-200 flex flex-col items-center">
                          {isGeneratingToken ? (
                              <div className="w-48 h-48 flex items-center justify-center flex-col bg-white rounded-[5px] shadow-inner mb-6">
                                  <Loader2 className="animate-spin text-blue-500 mb-2" size={32} />
                                  <span className="text-[10px] text-slate-400">リンクを生成しています。</span>
                              </div>
                          ) : generationError ? (
                               <div className="w-48 h-48 flex items-center justify-center flex-col bg-white rounded-[5px] border border-red-100 mb-6">
                                  <AlertCircle className="text-red-400 mb-2" size={32} />
                                  <button onClick={generateToken} className="text-xs font-bold text-blue-600 underline">再試行する</button>
                              </div>
                          ) : inviteUrl ? (
                              <div className="bg-white p-4 rounded-[5px] shadow-md mb-6">
                                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(inviteUrl)}`} alt="QR" className="w-36 h-36" />
                              </div>
                          ) : null}

                          <div className="w-full space-y-3">
                              <a href={inviteUrl} target="_blank" rel="noopener noreferrer" 
                                 className="w-full bg-[#06C755] text-white font-bold py-3 flex items-center justify-center shadow-md active:scale-95 transition-transform rounded-[4px]">
                                  <MessageCircle size={20} className="mr-2" />LINEを連携する
                              </a>
                              <button onClick={() => {navigator.clipboard.writeText(inviteUrl); showToast('コピーしました。')}} 
                                      className="w-full bg-white border border-gray-300 text-slate-600 py-2.5 rounded-[4px] text-xs font-bold hover:bg-gray-50 flex items-center justify-center">
                                  URLをコピー
                              </button>
                          </div>
                          
                          <div className="mt-6 flex flex-col items-center">
                              <div className="flex items-center text-orange-500 text-[10px] font-bold bg-orange-50 px-3 py-1.5 rounded-full animate-pulse mb-3">
                                  <Loader2 size={10} className="animate-spin mr-1.5" />
                                  連携の完了を待っています。
                              </div>
                              <button onClick={checkLineStatus} className="text-[10px] text-slate-400 hover:text-blue-600 underline">
                                  連携状況を確認
                              </button>
                          </div>
                      </div>
                  )}
              </div>
          );
          case 4: return (
              <div className="max-w-md mx-auto py-8 text-center animate-fade-in-up px-4">
                  <h2 className="text-xl font-bold text-slate-800 mb-2">ポジション設定</h2>
                  <p className="text-sm text-slate-500 mb-8 font-bold">「ホール」「キッチン」などの役割を登録します。<br/>（後からも登録が可能です。）</p>
                  <div className="bg-slate-50 p-6 rounded-[5px] border border-slate-200 space-y-4">
                      <div className="flex gap-2">
                          <input type="text" value={newPositionName} onChange={e => setNewPositionName(e.target.value)} placeholder="例：ホール" className="flex-1 border p-2.5 rounded-[4px] outline-none font-bold text-sm" />
                          <button onClick={handleAddPosition} className="bg-blue-600 text-white p-2.5 rounded-[4px] hover:bg-blue-700 transition-colors"><Plus /></button>
                      </div>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                          {positions.length === 0 && <p className="text-center py-4 text-slate-300 text-xs font-bold">ポジションが未登録です</p>}
                          {positions.map(p => <div key={p.id} className="flex justify-between items-center bg-white p-3 rounded-[4px] border text-sm font-bold shadow-sm">{p.name} <button onClick={() => { api.deletePosition(p.id).catch(console.error); setPositions(positions.filter(pos => pos.id !== p.id)); }} className="text-slate-300 hover:text-red-500"><Trash2 size={16}/></button></div>)}
                      </div>
                  </div>
              </div>
          );
          default: return <div className="py-12 text-center animate-scale-in"><PartyPopper size={64} className="mx-auto text-green-500 mb-4" /><h2 className="text-2xl font-bold text-slate-800 mb-2">設定が完了しました！</h2><p className="text-slate-500 text-sm font-bold leading-relaxed">全ての準備が整いました。<br/>これからシフト管理を始めましょう。</p></div>;
      }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <div className="bg-white border-b border-gray-200 pt-8 pb-12 px-4 shadow-sm z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between relative px-2">
              {STEPS.map((step, idx) => {
                  const Icon = step.icon;
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;
                  return (
                      <div key={step.id} className="flex flex-col items-center relative z-10 flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2 ${
                              isCompleted ? 'bg-blue-600 border-blue-600 text-white' : 
                              isActive ? 'bg-white border-blue-600 text-blue-600 shadow-lg shadow-blue-100' : 
                              'bg-white border-gray-200 text-gray-400'
                          }`}>
                              {isCompleted ? <Check size={20} strokeWidth={3} /> : <Icon size={20} />}
                          </div>
                          <span className={`text-[10px] mt-2 font-bold whitespace-nowrap absolute top-12 left-1/2 -translate-x-1/2 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                              {step.name}
                          </span>
                          {idx < STEPS.length - 1 && (
                              <div className="absolute left-[calc(50%+24px)] right-[calc(-50%+24px)] h-0.5 bg-gray-100 top-5 -z-10">
                                  <div className={`h-full bg-blue-600 transition-all duration-500 ${isCompleted ? 'w-full' : 'w-0'}`} />
                              </div>
                          )}
                      </div>
                  );
              })}
          </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {toast && (
            <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-full shadow-2xl bg-slate-900 text-white font-bold text-sm flex items-center animate-slide-up">
                {toast.type === 'success' ? <CheckCircle2 size={16} className="text-green-400 mr-2" /> : <AlertCircle size={16} className="text-red-400 mr-2" />}
                {toast.message}
            </div>
        )}

        <div className="w-full max-w-2xl bg-white rounded-[8px] shadow-xl border border-gray-100 overflow-hidden flex flex-col transition-all duration-300">
            <div className="flex-1 overflow-visible">
                {renderStepContent()}
            </div>

            <div className="px-8 py-5 bg-slate-50 border-t border-gray-100 flex justify-between items-center shrink-0">
                <button onClick={prevStep} className={`text-sm font-bold text-slate-400 hover:text-slate-600 transition-opacity ${currentStep > 1 && currentStep < 5 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    戻る
                </button>

                {currentStep < 5 ? (
                    <button 
                        onClick={currentStep === 2 ? handleSaveStoreAndAdmin : currentStep === 3 ? handleSaveChannelAndNext : nextStep} 
                        disabled={!canGoNext || isLoading} 
                        className="bg-blue-600 text-white font-bold py-3 px-10 rounded-[4px] hover:bg-blue-700 disabled:bg-slate-300 flex items-center shadow-lg shadow-blue-100 active:scale-95 transition-all"
                    >
                        {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : '次へ'}
                    </button>
                ) : (
                    <button onClick={handleCompleteSetup} disabled={isLoading} className="bg-green-600 text-white font-bold py-3 px-12 rounded-[4px] hover:bg-green-700 shadow-lg shadow-green-100 active:scale-95 transition-all flex items-center">
                        {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : '設定を完了して開始'}
                    </button>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default SetupPage;
